import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";

import { cn } from "@/lib/utils";

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

/**
 * Dims / restores the native status bar when dialogs open on Capacitor (business app only).
 * The Explore app manages its own status bar via ExploreStatusBarThemeSync, so we skip it.
 */
function useDialogStatusBar() {
  React.useEffect(() => {
    let disposed = false;

    void (async () => {
      try {
        const { Capacitor } = await import("@capacitor/core");
        if (!Capacitor.isNativePlatform()) return;

        // Avoid interfering with the Explore app's own status bar management.
        const { isExploreAppVariant } = await import("@/lib/platform");
        if (isExploreAppVariant()) return;

        if (disposed) return;
        const { StatusBar, Style } = await import("@capacitor/status-bar");
        const platform = Capacitor.getPlatform();
        if (platform === "android") {
          await StatusBar.setBackgroundColor({ color: "#111111" });
        }
        await StatusBar.setStyle({ style: Style.Light });
      } catch {
        /* StatusBar plugin unavailable */
      }
    })();

    return () => {
      disposed = true;
      void (async () => {
        try {
          const { Capacitor } = await import("@capacitor/core");
          if (!Capacitor.isNativePlatform()) return;

          const { isExploreAppVariant } = await import("@/lib/platform");
          if (isExploreAppVariant()) return;

          const { StatusBar, Style } = await import("@capacitor/status-bar");
          const platform = Capacitor.getPlatform();
          if (platform === "android") {
            await StatusBar.setBackgroundColor({ color: "#ffffff" });
          }
          await StatusBar.setStyle({ style: Style.Dark });
        } catch {
          /* StatusBar plugin unavailable */
        }
      })();
    };
  }, []);
}

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => {
  useDialogStatusBar();
  return (
    <DialogPrimitive.Overlay
      ref={ref}
      className={cn(
        "fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
        className,
      )}
      {...props}
    />
  );
});
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(
  (
    {
      className,
      children,
      style,
      onInteractOutside,
      onPointerDownOutside,
      onFocusOutside,
      ...props
    },
    ref,
  ) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      style={{
        top: "calc(50% + env(safe-area-inset-top) / 2)",
        ...style,
      }}
      className={cn(
        "fixed left-[50%] z-50 grid w-[calc(100vw-3rem)] sm:w-full sm:max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-4 sm:p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] rounded-2xl",
        className,
      )}
      onPointerDownOutside={(e) => {
        // Google Places Autocomplete dropdown is rendered outside the dialog (attached to <body>).
        // Radix closes dialogs on pointer-down outside by default; that can cancel the selection click.
        const t = ((e as any)?.detail?.originalEvent?.target ?? e.target) as HTMLElement | null;
        if (t?.closest?.(".pac-container")) {
          e.preventDefault();
          return;
        }
        onPointerDownOutside?.(e);
      }}
      onFocusOutside={(e) => {
        const t = ((e as any)?.detail?.originalEvent?.target ?? e.target) as HTMLElement | null;
        if (t?.closest?.(".pac-container")) {
          e.preventDefault();
          return;
        }
        onFocusOutside?.(e);
      }}
      onInteractOutside={(e) => {
        // Google Places Autocomplete dropdown is rendered outside the dialog (attached to <body>).
        // Without this guard, clicking a suggestion is treated as an outside interaction and the
        // dialog may close / swallow the click, making suggestions unclickable.
        const t = ((e as any)?.detail?.originalEvent?.target ?? e.target) as HTMLElement | null;
        if (t?.closest?.(".pac-container")) {
          e.preventDefault();
          return;
        }
        onInteractOutside?.(e);
      }}
      {...props}
    >
      {children}
      <DialogPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity data-[state=open]:bg-accent data-[state=open]:text-muted-foreground hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none">
        <X className="h-4 w-4" />
        <span className="sr-only">Close</span>
      </DialogPrimitive.Close>
    </DialogPrimitive.Content>
  </DialogPortal>
));
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("flex flex-col space-y-1.5 text-center sm:text-left", className)} {...props} />
);
DialogHeader.displayName = "DialogHeader";

const DialogFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("flex flex-col-reverse gap-2 sm:flex-row sm:justify-end", className)} {...props} />
);
DialogFooter.displayName = "DialogFooter";

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold leading-none tracking-tight", className)}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description ref={ref} className={cn("text-sm text-muted-foreground", className)} {...props} />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
};
